// Small, purposeful mapping of `university_id` -> best-known primary domain.
// We use logo.clearbit.com which returns a high-quality PNG/SVG logo for any
// public domain. It's free, no auth, CDN-cached, and gracefully returns 404
// which we fall back to via <img onError>.
const UNI_DOMAINS = {
  "uk-oxford":        "ox.ac.uk",
  "uk-cambridge":     "cam.ac.uk",
  "uk-lse":           "lse.ac.uk",
  "uk-imperial":      "imperial.ac.uk",
  "uk-warwick":       "warwick.ac.uk",
  "uk-ucl":           "ucl.ac.uk",
  "uk-edinburgh":     "ed.ac.uk",
  "uk-kcl":           "kcl.ac.uk",
  "uk-bristol":       "bristol.ac.uk",
  "uk-manchester":    "manchester.ac.uk",
  "uk-bath":          "bath.ac.uk",
  "uk-durham":        "durham.ac.uk",
  "uk-st-andrews":    "st-andrews.ac.uk",
  "uk-nottingham":    "nottingham.ac.uk",
  "uk-leeds":         "leeds.ac.uk",
  "uk-sheffield":     "sheffield.ac.uk",
  "uk-birmingham":    "birmingham.ac.uk",
  "uk-glasgow":       "gla.ac.uk",
  "uk-exeter":        "exeter.ac.uk",
  "uk-southampton":   "southampton.ac.uk",
  "uk-york":          "york.ac.uk",
  "uk-loughborough":  "lboro.ac.uk",
  "uk-lancaster":     "lancaster.ac.uk",
  "uk-newcastle":     "ncl.ac.uk",
  "uk-qmul":          "qmul.ac.uk",
  "us-mit":           "mit.edu",
  "us-stanford":      "stanford.edu",
  "us-harvard":       "harvard.edu",
  "us-berkeley":      "berkeley.edu",
  "us-yale":          "yale.edu",
  "us-princeton":     "princeton.edu",
  "us-columbia":      "columbia.edu",
  "us-chicago":       "uchicago.edu",
  "us-penn":          "upenn.edu",
  "us-cornell":       "cornell.edu",
  "us-northwestern":  "northwestern.edu",
  "us-duke":          "duke.edu",
  "us-brown":         "brown.edu",
  "us-dartmouth":     "dartmouth.edu",
  "us-rice":          "rice.edu",
  "us-vanderbilt":    "vanderbilt.edu",
  "us-notre-dame":    "nd.edu",
  "us-georgetown":    "georgetown.edu",
  "us-usc":           "usc.edu",
  "us-umich":         "umich.edu",
  "us-ucla":          "ucla.edu",
  "us-nyu":           "nyu.edu",
  "us-cmu":           "cmu.edu",
  "us-jhu":           "jhu.edu",
  "us-emory":         "emory.edu",
  "de-tum":           "tum.de",
  "de-lmu":           "lmu.de",
  "de-heidelberg":    "uni-heidelberg.de",
  "de-rwth":          "rwth-aachen.de",
  "fr-psl":           "psl.eu",
  "fr-sciences-po":   "sciencespo.fr",
  "fr-polytechnique": "polytechnique.edu",
  "fr-hec":           "hec.edu",
};

// Signature brand colours (deep, editorial) — used as the tile background when
// no logo art is available. Chosen to feel like university crests, not tech
// gradients.
const UNI_BRAND = {
  "uk-oxford":        { bg: "#002147", fg: "#F7EFCF" },  // Oxford navy + parchment
  "uk-cambridge":     { bg: "#A3C1AD", fg: "#0B2D3B" },  // Cambridge blue
  "uk-lse":           { bg: "#7B1E3B", fg: "#F5E9EA" },  // LSE crimson
  "uk-imperial":      { bg: "#003E74", fg: "#DDEEF6" },  // Imperial blue
  "uk-warwick":       { bg: "#582C83", fg: "#EFE6F5" },  // Warwick purple
  "uk-ucl":           { bg: "#500778", fg: "#F5E7C4" },  // UCL purple + gold
  "uk-edinburgh":     { bg: "#041E42", fg: "#D50032" },  // Edinburgh navy + red
  "uk-kcl":           { bg: "#B71E2C", fg: "#F5E9EA" },  // King's red
  "uk-bristol":       { bg: "#AB2A3E", fg: "#F5E9EA" },  // Bristol red
  "uk-manchester":    { bg: "#660D20", fg: "#E7B44C" },  // Manchester maroon + gold
  "uk-bath":          { bg: "#3E1E5F", fg: "#F5E7C4" },  // Bath purple
  "uk-durham":        { bg: "#68246D", fg: "#F5E7C4" },  // Durham palatinate
  "uk-st-andrews":    { bg: "#00539B", fg: "#F5E9EA" },  // St Andrews blue
  "uk-nottingham":    { bg: "#00A499", fg: "#FFFFFF" },  // Nottingham teal
  "uk-leeds":         { bg: "#004E5F", fg: "#FCE300" },  // Leeds teal + yellow
  "uk-sheffield":     { bg: "#0F91FF", fg: "#F5E9EA" },  // Sheffield blue
  "uk-birmingham":    { bg: "#3C1053", fg: "#FFA400" },  // Birmingham purple + amber
  "uk-glasgow":       { bg: "#003865", fg: "#F5E7C4" },  // Glasgow navy
  "uk-exeter":        { bg: "#022851", fg: "#F5C518" },  // Exeter navy + gold
  "uk-southampton":   { bg: "#005C84", fg: "#F5E9EA" },  // Southampton blue
  "uk-york":          { bg: "#005364", fg: "#F5E9EA" },  // York teal
  "uk-loughborough":  { bg: "#59316B", fg: "#F5E7C4" },  // Loughborough purple
  "uk-lancaster":     { bg: "#B5121B", fg: "#F5E9EA" },  // Lancaster red
  "uk-newcastle":     { bg: "#00263A", fg: "#F5E7C4" },  // Newcastle navy
  "uk-qmul":          { bg: "#2C6E48", fg: "#F5E9EA" },  // Queen Mary green
  "us-mit":           { bg: "#8A1B2E", fg: "#EFEFEF" },  // MIT cardinal + silver
  "us-stanford":      { bg: "#8C1515", fg: "#F5E9EA" },  // Stanford cardinal
  "us-harvard":       { bg: "#A51C30", fg: "#F1E4C7" },  // Harvard crimson
  "us-berkeley":      { bg: "#003262", fg: "#FDB515" },  // Berkeley blue + gold
  "us-yale":          { bg: "#0F4D92", fg: "#F5E7C4" },  // Yale blue
  "us-princeton":     { bg: "#E77500", fg: "#1E1E1E" },  // Princeton orange + black
  "us-columbia":      { bg: "#B9D9EB", fg: "#0B2D3B" },  // Columbia blue
  "us-chicago":       { bg: "#800000", fg: "#F5E7C4" },  // UChicago maroon
  "us-penn":          { bg: "#011F5B", fg: "#990000" },  // Penn navy + red
  "us-cornell":       { bg: "#B31B1B", fg: "#F5E7C4" },  // Cornell red
  "us-northwestern":  { bg: "#4E2A84", fg: "#F5E7C4" },  // NU purple
  "us-duke":          { bg: "#012169", fg: "#F5E9EA" },  // Duke blue
  "us-brown":         { bg: "#4E3629", fg: "#F5E7C4" },  // Brown brown
  "us-dartmouth":     { bg: "#00693E", fg: "#F5E7C4" },  // Dartmouth green
  "us-rice":          { bg: "#00205B", fg: "#C1C6C8" },  // Rice blue + silver
  "us-vanderbilt":    { bg: "#866D4B", fg: "#231F20" },  // Vanderbilt gold + black
  "us-notre-dame":    { bg: "#0C2340", fg: "#C99700" },  // ND navy + gold
  "us-georgetown":    { bg: "#041E42", fg: "#8B8680" },  // Georgetown blue + grey
  "us-usc":           { bg: "#990000", fg: "#FFCC00" },  // USC cardinal + gold
  "us-umich":         { bg: "#00274C", fg: "#FFCB05" },  // Michigan maize + blue
  "us-ucla":          { bg: "#2774AE", fg: "#FFD100" },  // UCLA blue + gold
  "us-nyu":           { bg: "#57068C", fg: "#F5E7C4" },  // NYU violet
  "us-cmu":           { bg: "#C41230", fg: "#F5E7C4" },  // CMU red
  "us-jhu":           { bg: "#002D72", fg: "#F5E7C4" },  // JHU blue
  "us-emory":         { bg: "#012169", fg: "#F5C518" },  // Emory blue + gold
  "de-tum":           { bg: "#0065BD", fg: "#FFFFFF" },  // TUM blue
  "de-lmu":           { bg: "#00883A", fg: "#FFF6D0" },  // LMU green
  "de-heidelberg":    { bg: "#B01F24", fg: "#F5E9EA" },  // Heidelberg red
  "de-rwth":          { bg: "#00549F", fg: "#FFFFFF" },  // RWTH blue
  "fr-psl":           { bg: "#1D1D1B", fg: "#F5F0E1" },  // PSL near-black
  "fr-sciences-po":   { bg: "#1B1F3A", fg: "#F5F0E1" },  // Sciences Po ink
  "fr-polytechnique": { bg: "#003D75", fg: "#F5C518" },  // X blue + straw
  "fr-hec":           { bg: "#2E2A5D", fg: "#F5F0E1" },  // HEC navy
};

// Countries where we intentionally do NOT auto-fetch a logo/crest —
// unofficial reproductions of PRC / Indian university crests carry
// trademark risk. These fall through to the initials tile.
const LETTERS_ONLY_COUNTRIES = new Set(["China", "India"]);

const DEFAULT_BRAND = { bg: "#44403C", fg: "#F5F0E1" };  // stone-700 / cream

/**
 * Return the best available logo URL for a university.
 *
 * Resolution order:
 *   1. `stored` — a Mongo-cached URL (populated by
 *      `scripts/fetch_uni_logos.py` from Wikipedia's REST API). This is
 *      the highest-quality option because Wikipedia crests are usually
 *      the actual university coat-of-arms in SVG or high-res PNG.
 *   2. `google-favicon` — deterministic favicon URL for any known
 *      domain. Reliable but favicon-quality (64px, sometimes fuzzy).
 *      Falls back to the domain in `UNI_DOMAINS` or, if provided by the
 *      caller, an `officialDomain` from `universities.official_domains`.
 *   3. `null` — no URL. The caller renders the initials-tile fallback.
 *
 * Callers pass the whole uni doc so we can honour `logo_url`,
 * `official_domains`, and `country` without needing extra round-trips.
 */
export function getUniLogoUrl(uniId, opts = {}) {
  const { storedUrl = null, officialDomain = null, country = null } = opts;
  if (storedUrl) return storedUrl;
  // Editorial guard-rail: never auto-serve unofficial crests for these
  // markets. The initials tile keeps the row visually intact.
  if (country && LETTERS_ONLY_COUNTRIES.has(country)) return null;
  const domain = officialDomain || UNI_DOMAINS[uniId];
  if (!domain) return null;
  // Google's favicon service is free, unauthenticated, CDN-cached, and
  // has been stable for a decade. sz=128 is the largest usable size.
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
}

export function getUniBrand(uniId) {
  return UNI_BRAND[uniId] || DEFAULT_BRAND;
}

// A stable, short display initial for the crest-fallback pill (never > 3 chars).
export function getUniInitials(uniName) {
  if (!uniName) return "?";
  const clean = uniName.replace(/\(.*?\)/g, "").trim();
  const words = clean.split(/\s+/).filter(w => w.length > 2 && !/^(of|the|and|de|la|le)$/i.test(w));
  if (words.length === 0) return clean.slice(0, 2).toUpperCase();
  return words.slice(0, 2).map(w => w[0]).join("").toUpperCase();
}
